import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AuthenticationService } from '../../../core/authentication/authentication.service';
import { LoginComponent } from '../../../core/authentication/components/login/login.component';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { ResourceEnum, RightsEnum, UserGroupEnum } from '../../../core/authorization/models';
import { FeatureFlags, FeatureFlagService } from '../../../core/feature-flags';
import { TenantConfigService } from '../../../core/tenant/tenant-config.service';
import { ProfileComponent } from '../../../shared/components/profile/profile.component';
import { ChangePasswordComponent } from '../../../shared/components/change-password/change-password.component';

/**
 * Navigation menu component providing site-wide navigation and user actions.
 * Adapts displayed options based on user authentication state and permissions.
 */
@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrl: './nav-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    MatTooltipModule,
  ],
})
export class NavMenuComponent {
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly tenantService = inject(TenantConfigService);
  private readonly featureFlagService = inject(FeatureFlagService);

  /** Reactive feature-flag signal for the Position Sets menu item. */
  protected readonly positionSetsFeatureEnabled = this.featureFlagService.hasFlag(
    FeatureFlags.CustomPositions,
  );

  /** Expose enums to template */
  protected readonly resourceEnum = ResourceEnum;
  protected readonly rightsEnum = RightsEnum;
  protected readonly FeatureFlags = FeatureFlags;

  /** Signal references from authentication service */
  protected readonly loggedIn = this.authService.loggedIn;
  protected readonly currentUser = this.authService.currentUser;

  /** User home route signal from authorization service */
  protected readonly userHome = this.authorizationService.userHome;

  /** SysAdmin signal from authorization service (exposed for template gating) */
  protected readonly isSysAdmin = this.authorizationService.isSysAdmin;

  /** Tenant branding signals */
  protected readonly logoUrl = this.tenantService.logoUrl;
  protected readonly appTitle = this.tenantService.appTitle;
  protected readonly tenantName = this.tenantService.displayName;

  /** Computed caption for login/logout button */
  protected readonly loggedInCaption = computed(() => (this.loggedIn() ? 'Logout' : 'Login'));

  /** Computed icon for login/logout button */
  protected readonly loggedInIcon = computed(() => (this.loggedIn() ? 'lock_open' : 'lock'));

  /** Computed signal to check if current user is a rater */
  protected readonly isRater = computed(() => {
    const user = this.currentUser();
    return (
      user?.appUser.userGroups?.some((g) => g.userGroupId === UserGroupEnum.Rater) ?? false
    );
  });

  /** Computed signal to check if current user is a participant */
  protected readonly isParticipant = computed(() => {
    const user = this.currentUser();
    return (
      user?.appUser.userGroups?.some((g) => g.userGroupId === UserGroupEnum.Participant) ?? false
    );
  });

  /** Computed signal to check if current user is a client admin */
  protected readonly isClient = computed(() => {
    const user = this.currentUser();
    return (
      user?.appUser.userGroups?.some((g) => g.userGroupId === UserGroupEnum.ClientAdmin) ?? false
    );
  });

  /** Computed signal to check if current user is only a client (not admin) */
  protected readonly isClientOnly = computed(() => {
    const user = this.currentUser();
    if (!user) return false;

    const isClientAdmin = user.appUser.userGroups?.some(
      (g) => g.userGroupId === UserGroupEnum.ClientAdmin,
    );
    const isAccountAdmin = user.appUser.userGroups?.some(
      (g) => g.userGroupId === UserGroupEnum.AccountAdmin,
    );
    const isSysAdmin = user.appUser.userGroups?.some(
      (g) => g.userGroupId === UserGroupEnum.SysAdmin,
    );

    return isClientAdmin && !isAccountAdmin && !isSysAdmin;
  });

  /**
   * Opens login dialog or logs out based on current state.
   */
  login(): void {
    if (!this.loggedIn()) {
      this.dialog.open(LoginComponent, this.authService.loginDialogConfig);
    } else {
      this.logout();
    }
  }

  /**
   * Logs out the current user.
   */
  logout(): void {
    this.authService.logout();
  }

  /**
   * Opens the profile editing dialog.
   */
  profile(): void {
    this.dialog.open(ProfileComponent);
  }

  /**
   * Opens the change password dialog.
   */
  changePassword(): void {
    this.dialog.open(ChangePasswordComponent);
  }

  /**
   * Checks if the current user has access to a specific resource.
   * @param resource - The resource to check access for
   * @param rights - The rights level required (defaults to Create)
   * @returns True if user has access
   */
  hasAccess(resource: ResourceEnum, rights: RightsEnum = RightsEnum.Create): boolean {
    return this.authorizationService.hasRightsToResource(resource, rights);
  }
}
